const toString = Object.prototype.toString

export function is(val: unknown, type: string) {
	return toString.call(val) === `[object ${type}]`
}

export function isDef<T = unknown>(val?: T): val is T {
	return typeof val !== 'undefined'
}

export function isUnDef<T = unknown>(val?: T): val is T {
	return !isDef(val)
}

export function isObject(val: any): val is Record<any, any> {
	return val !== null && is(val, 'Object')
}

export function isEmpty<T = unknown>(val: T): val is T {
	if (isArray(val) || isString(val)) {
		return val.length === 0
	}

	if (val instanceof Map || val instanceof Set) {
		return val.size === 0
	}

	if (isObject(val)) {
		return Object.keys(val).length === 0
	}

	return false
}

// export function isDate(val: unknown): val is Date {
// 	return is(val, "Date")
// }

export function isNull(val: unknown): val is null {
	return val === null
}

export function isNullAndUnDef(val: unknown): val is null | undefined {
	return isUnDef(val) && isNull(val)
}

export function isNullOrUnDef(val: unknown): val is null | undefined {
	return isUnDef(val) || isNull(val)
}

export function isNumber(val: unknown): val is number {
	return is(val, 'Number')
}

export function isPromise<T = any>(val: unknown): val is Promise<T> {
	return is(val, 'Promise') && isObject(val) && isFunction(val.then) && isFunction(val.catch)
}

export function isString(val: unknown): val is string {
	return is(val, 'String')
}

export function isFunction(val: unknown): val is Function {
	return typeof val === 'function'
}

export function isBoolean(val: unknown): val is boolean {
	return is(val, 'Boolean')
}

export function isRegExp(val: unknown): val is RegExp {
	return is(val, 'RegExp')
}

export function isArray(val: any): val is Array<any> {
	return val && Array.isArray(val)
}

export function isWindow(val: any): val is Window {
	return typeof window !== 'undefined' && is(val, 'Window')
}

export function isElement(val: unknown): val is Element {
	return isObject(val) && !!val.tagName
}

export function isMap(val: unknown): val is Map<any, any> {
	return is(val, 'Map')
}

export const isServer = typeof window === 'undefined'

export const isClient = !isServer

export function isUrl(path: string): boolean {
	const reg =
		/(((^https?:(?:\/\/)?)(?:[-;:&=\+\$,\w]+@)?[A-Za-z0-9.-]+(?::\d+)?|(?:www.|[-;:&=\+\$,\w]+@)[A-Za-z0-9.-]+)((?:\/[\+~%\/.\w-_]*)?\??(?:[-\+=&;%@.\w_]*)#?(?:[\w]*))?)$/
	return reg.test(path)
}

/**
 * @function 深度对比对象属性是否相同
 * @description 该函数实现深度对比对象的属性是否相同，只对比对象，数组这种特殊的Object类型不做对比
 * @param o1 {object|undefined} o1是Object类型的参数,在函数递归的时候有可能为undefined
 * @param o2 {object|undefined} 02是Object类型的参数,在函数递归的时候有可能为undefined
 * @return false|true :返回值为false代表两个对象属性有不相同的地方，true证明两个对象属性全部相同
 * @author JoKer 2021/09/10
 * @version 1.0.1
 * @example
 */
export function isObjPropsEqual(o1: DefaultClass, o2: DefaultClass) {
	let flag = true
	function isDiff(oldObj, newObj) {
		try {
			if (oldObj.constructor !== newObj.constructor) {
				throw new TypeError("两个参数的类型必须都为'Object'")
			}
			const oldKeys = Object.keys(oldObj)
			const newKeys = Object.keys(newObj)
			const difference = oldKeys.filter(x => newKeys.indexOf(x) === -1).concat(newKeys.filter(x => oldKeys.indexOf(x) === -1))
			if (difference.length !== 0) {
				// difference数组存储两个对象不同的属性（同一层）
				flag = false
			}
			if (oldKeys.length === newKeys.length) {
				// 同一层元素属性一样多才进行递归
				newKeys.forEach(e => {
					if (oldObj[e]?.constructor === Object && newObj[e]?.constructor === Object) {
						// 两个参数的属性都为object才继续进行对比
						isDiff(oldObj[e], newObj[e])
					}
				})
			}
			return flag
		} catch (e) {
			console.error('对比时出错', e)
		}
	}
	return isDiff(o1, o2)
}

/**
 * @function 深度对比对象值是否相同
 * @param x {Object} 对象1
 * @param y {Object} 对象2
 * @return  {Boolean} true 为相等，false 为不等
 */
export function isObjEqual(x: DefaultClass, y: DefaultClass) {
	// 指向同一内存时 直接返回true
	if (x === y) {
		return true
	}
	if (typeof x === 'object' && x !== null && typeof y === 'object' && y !== null) {
		if (Object.keys(x).length !== Object.keys(y).length) {
			// 键的数量不相等，直接返回false
			return false
		}
		for (const prop in x) {
			if (y.hasOwnProperty(prop)) {
				if (!isObjEqual(x[prop], y[prop])) return false
			} else {
				return false
			}
		}
		return true
	}
	return false
}
